/**
 * Contract for agent memory, org knowledge (Obsidian vault) and the master chat.
 * Imports from api.ts are type-only: api.ts spreads `KNOWLEDGE_ROUTES` into `ROUTES`.
 */
import { z } from 'zod';
import type { RouteDef } from './api.js';
import { CHAT_ATTACHMENTS_PER_MESSAGE } from './chat.js';
import { RunSettingsOverride } from './run-settings.js';

const Id = z.string().min(1).max(64);

/** Hidden per-org agent behind the master chat (kind ephemeral → not listed as an agent). */
export const MASTER_AGENT_SLUG = '__company';
/** Slugs with this prefix are platform-owned system agents (never learn, never listed). */
export const SYSTEM_AGENT_PREFIX = '__';

export const MEMORY_TYPES = ['episodic', 'semantic', 'procedural'] as const;
export const MemoryType = z.enum(MEMORY_TYPES);
export type MemoryType = z.infer<typeof MemoryType>;
export const MEMORY_SCOPES = ['agent', 'team', 'org'] as const;
export const MemoryScope = z.enum(MEMORY_SCOPES);
export const MEMORY_TRUST = ['user', 'agent', 'untrusted'] as const;

// ---------- Memory ----------
export const MemoryDto = z.object({
  id: Id,
  agentId: Id.nullable(),
  scope: MemoryScope,
  type: MemoryType,
  title: z.string(),
  content: z.string(),
  tags: z.array(z.string()),
  trust: z.enum(MEMORY_TRUST),
  /** tool | reflection | user | vault | consolidation */
  source: z.string(),
  sourceRunId: Id.nullable(),
  pinned: z.boolean(),
  utility: z.number(),
  retrievalCount: z.number(),
  hitCount: z.number(),
  tokens: z.number(),
  version: z.number(),
  vaultPath: z.string().nullable(),
  archived: z.boolean(),
  lastUsedAt: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type MemoryDto = z.infer<typeof MemoryDto>;

export const MemoriesQuery = z.object({
  q: z.string().max(500).optional(),
  type: MemoryType.optional(),
  scope: MemoryScope.optional(),
  source: z.string().max(20).optional(),
  pinned: z.enum(['true', 'false']).optional(),
  includeArchived: z.enum(['true', 'false']).default('false'),
  limit: z.coerce.number().int().min(1).max(500).default(200),
});

export const MemoryInput = z.object({
  /** null = org knowledge. */
  agentId: Id.nullable().default(null),
  scope: MemoryScope.default('agent'),
  type: MemoryType.default('semantic'),
  title: z.string().max(120).optional(),
  content: z.string().min(1).max(4000),
  tags: z.array(z.string().max(50)).max(10).default([]),
  pinned: z.boolean().default(false),
});
export type MemoryInput = z.input<typeof MemoryInput>;

export const MemoryPatch = z.object({
  title: z.string().max(120).optional(),
  content: z.string().min(1).max(4000).optional(),
  type: MemoryType.optional(),
  tags: z.array(z.string().max(50)).max(10).optional(),
  pinned: z.boolean().optional(),
  archived: z.boolean().optional(),
});
export type MemoryPatch = z.infer<typeof MemoryPatch>;

export const MemoryRevisionDto = z.object({
  id: Id,
  version: z.number(),
  title: z.string(),
  content: z.string(),
  reason: z.string(),
  createdBy: z.string().nullable(),
  createdAt: z.string(),
});
export type MemoryRevisionDto = z.infer<typeof MemoryRevisionDto>;

export const AgentLearningDto = z.object({
  agentId: Id,
  learningEnabled: z.boolean(),
  playbook: z.string(),
  playbookTokens: z.number(),
  playbookUpdatedAt: z.string().nullable(),
  consolidatedAt: z.string().nullable(),
  reflections: z.number(),
  reflectionsSkipped: z.number(),
  /** All background learning cost of this agent (reflection + consolidation + embeddings), USD. */
  reflectionCostUsd: z.number(),
  memoryCount: z.number(),
  byType: z.object({ episodic: z.number(), semantic: z.number(), procedural: z.number() }),
  cap: z.number(),
});
export type AgentLearningDto = z.infer<typeof AgentLearningDto>;
export const AgentLearningPatch = z.object({ learningEnabled: z.boolean() });

// ---------- Knowledge settings / vault ----------
export const KnowledgeSettingsDto = z.object({
  learningEnabled: z.boolean(),
  reflectionModelRef: z.string().nullable(),
  maxLessonsPerRun: z.number(),
  reflectionInputTokens: z.number(),
  reflectionOutputTokens: z.number(),
  dailyReflectionTokens: z.number(),
  dailyReflectionUsd: z.number(),
  injectTokens: z.number(),
  playbookTokens: z.number(),
  topK: z.number(),
  agentMemoryCap: z.number(),
  consolidationEnabled: z.boolean(),
  vaultEnabled: z.boolean(),
  vaultImportFolder: z.string(),
  obsidianRestEnabled: z.boolean(),
  obsidianRestUrl: z.string().nullable(),
  /** "••••a1b2" or null. The key itself is write-only. */
  obsidianRestKeyHint: z.string().nullable(),
  obsidianRestCa: z.string().nullable(),
  masterModelRef: z.string().nullable(),
  masterMessageTokens: z.number(),
  /** Today's learning spend (reflection + consolidation). */
  spentTodayTokens: z.number(),
  spentTodayUsd: z.number(),
});
export type KnowledgeSettingsDto = z.infer<typeof KnowledgeSettingsDto>;

const ImportFolder = z
  .string()
  .min(1)
  .max(120)
  .regex(/^[A-Za-z0-9 _-]+(?:\/[A-Za-z0-9 _-]+)*$/, 'relative folder, letters/digits/space/_/- only');

export const KnowledgeSettingsPatch = z.object({
  learningEnabled: z.boolean().optional(),
  reflectionModelRef: z.string().max(200).nullable().optional(),
  maxLessonsPerRun: z.number().int().min(0).max(10).optional(),
  reflectionInputTokens: z.number().int().min(300).max(8000).optional(),
  reflectionOutputTokens: z.number().int().min(100).max(2000).optional(),
  dailyReflectionTokens: z.number().int().min(0).max(10_000_000).optional(),
  dailyReflectionUsd: z.number().min(0).max(100).optional(),
  injectTokens: z.number().int().min(0).max(4000).optional(),
  playbookTokens: z.number().int().min(0).max(2000).optional(),
  topK: z.number().int().min(0).max(20).optional(),
  agentMemoryCap: z.number().int().min(10).max(10_000).optional(),
  consolidationEnabled: z.boolean().optional(),
  vaultEnabled: z.boolean().optional(),
  vaultImportFolder: ImportFolder.optional(),
  obsidianRestEnabled: z.boolean().optional(),
  obsidianRestUrl: z.string().url().max(300).nullable().optional(),
  /** Write-only; null removes the stored key. */
  obsidianRestApiKey: z.string().min(8).max(500).nullable().optional(),
  obsidianRestCa: z.string().max(20_000).nullable().optional(),
  masterModelRef: z.string().max(200).nullable().optional(),
  masterMessageTokens: z.number().int().min(1000).max(200_000).optional(),
});
export type KnowledgeSettingsPatch = z.infer<typeof KnowledgeSettingsPatch>;

export const VaultStatusDto = z.object({
  enabled: z.boolean(),
  /** Org folder inside the vault root as reported by the worker (null until the first sync). */
  path: z.string().nullable(),
  importFolder: z.string(),
  lastSyncAt: z.string().nullable(),
  lastError: z.string().nullable(),
  requestedAt: z.string().nullable(),
  notes: z.number(),
  exported: z.number(),
  imported: z.number(),
  conflicts: z.number(),
  log: z.array(z.record(z.string(), z.unknown())),
});
export type VaultStatusDto = z.infer<typeof VaultStatusDto>;

// ---------- Master chat ----------
export const MasterModelDto = z.object({
  modelRef: z.string(),
  displayName: z.string(),
  providerType: z.string(),
  /** USD per 1M tokens. */
  inputPrice: z.number().nullable(),
  outputPrice: z.number().nullable(),
  /** Typical small call (1.5k in / 350 out), USD. */
  estCallUsd: z.number().nullable(),
  isDefault: z.boolean(),
});
export type MasterModelDto = z.infer<typeof MasterModelDto>;

export const MasterConversationDto = z.object({
  id: Id,
  title: z.string(),
  modelRef: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type MasterConversationDto = z.infer<typeof MasterConversationDto>;
export const MasterConversationInput = z.object({ modelRef: z.string().max(200).optional() }).default({});
export const MasterConversationPatch = z.object({ modelRef: z.string().max(200).nullable() });

export const MasterSendInput = z
  .object({
    runSettings: RunSettingsOverride.optional(),
    text: z.string().max(20_000).default(''),
    /** Overrides the conversation's model for this and later messages (must be enabled). */
    modelRef: z.string().max(200).optional(),
    /** Screenshots uploaded before sending: the message then always goes to the model. */
    attachmentIds: z.array(z.string().uuid()).max(CHAT_ATTACHMENTS_PER_MESSAGE).optional(),
  })
  .refine((b) => b.text.trim().length > 0 || (b.attachmentIds?.length ?? 0) > 0, {
    message: 'text or an attachment is required',
    path: ['text'],
  });

/** How the router handled a message: deterministic paths cost $0. */
export const MASTER_ROUTES = ['command', 'intent', 'delegation', 'llm'] as const;
export const MasterSendResult = z.object({
  messageId: Id,
  route: z.enum(MASTER_ROUTES),
  /** Router reply (command/intent/delegation) — already persisted. */
  replyMessageId: Id.nullable(),
  /** Model run (llm) or the delegated agent's run (delegation). */
  runId: Id.nullable(),
  costUsd: z.number(),
});
export type MasterSendResult = z.infer<typeof MasterSendResult>;

/** How a run ended in its own words (`report_result.outcome`); absent on older results. */
export const RESULT_OUTCOMES = ['done', 'partial', 'blocked'] as const;
export type ResultOutcome = (typeof RESULT_OUTCOMES)[number];

/**
 * A run's reported result (`runs.result` / `tasks.result` jsonb, written by `report_result`). Everything
 * but `summary` is optional, so older rows stay valid. A type alias (not an interface) so it stays
 * assignable to the API's open `{ summary, [k]: unknown }` DTOs.
 */
export type RunResult = {
  summary: string;
  artifacts?: unknown[];
  outcome?: ResultOutcome;
  /** Short references backing the result: URLs, run ids, file paths, tool results. */
  evidence?: string[];
  openQuestions?: string[];
};

/**
 * A delegated task's outcome, reported back to whoever delegated it: a `report` card in the company
 * chat, or a `{ type: 'data-af-report', data }` part on the report message in an agent's chat.
 */
export interface TaskReport {
  taskId: string;
  runId: string;
  title: string;
  agentId: string;
  agentName: string;
  status: 'done' | 'failed' | 'cancelled';
  summary: string | null;
  error: string | null;
  artifacts?: { name: string; content?: string; url?: string }[];
  outcome?: ResultOutcome;
  evidence?: string[];
  openQuestions?: string[];
}

/** Result cards inside assistant message parts: `{ type: 'data-af-card', data: MasterCard }`. */
export type MasterCard =
  | { kind: 'help'; commands: typeof MASTER_COMMANDS }
  | {
      kind: 'status';
      halted: boolean;
      activeRuns: number;
      pendingApprovals: number;
      costTodayUsd: number;
      costMonthUsd: number;
      agents: { active: number; paused: number };
    }
  | { kind: 'agents'; items: MasterAgentItem[] }
  | { kind: 'agent'; agent: MasterAgentItem; runs: MasterRunItem[] }
  | { kind: 'runs'; title: string; items: MasterRunItem[] }
  | { kind: 'results'; title: string; items: MasterResultItem[] }
  | { kind: 'approvals'; items: MasterApprovalItem[] }
  | {
      kind: 'budget';
      costTodayUsd: number;
      costMonthUsd: number;
      learningTodayUsd: number;
      budgets: {
        scope: string;
        agent: string | null;
        period: string;
        limitUsd: number | null;
        spentUsd: number;
      }[];
      topAgents: { agent: string; costUsd: number }[];
    }
  | { kind: 'delegation'; agentId: string; agentName: string; taskId: string; runId: string; title: string }
  | ({ kind: 'report' } & TaskReport)
  | { kind: 'decided'; approvalId: string; approved: boolean; toolName: string }
  | { kind: 'error'; message: string };

export interface MasterAgentItem {
  id: string;
  name: string;
  slug: string;
  roleTitle: string;
  status: string;
  activeRuns: number;
  costTodayUsd: number;
}
export interface MasterRunItem {
  id: string;
  agentId: string;
  agentName: string;
  mode: string;
  status: string;
  costUsd: number;
  title: string | null;
  error: string | null;
  createdAt: string;
}
export interface MasterResultItem {
  taskId: string;
  runId: string | null;
  agentName: string;
  title: string;
  status: string;
  summary: string;
  costUsd: number;
  completedAt: string | null;
}
export interface MasterApprovalItem {
  id: string;
  runId: string;
  agentName: string;
  toolName: string;
  inputPreview: string;
  shortToken: string;
  expiresAt: string;
}

/** Slash commands answered from the database (no model call). */
export const MASTER_COMMANDS = [
  { cmd: '/status', args: '', desc: 'Org status: active runs, approvals, cost today' },
  { cmd: '/agents', args: '', desc: 'All agents with status and cost today' },
  { cmd: '/runs', args: '[agent] [status]', desc: 'Recent runs, optionally per agent or status' },
  { cmd: '/results', args: '<agent|task>', desc: 'Latest task results of an agent or matching a task' },
  { cmd: '/approve', args: '[token] [deny]', desc: 'Pending approvals, or approve/deny one by token' },
  { cmd: '/budget', args: '', desc: 'Spend today/this month and budgets' },
  { cmd: '/task', args: '<agent> <text>', desc: 'Create a task for an agent (runs immediately)' },
  { cmd: '/help', args: '', desc: 'This list' },
] as const;

// ---------- Jobs ----------
export const MEMORY_QUEUE = 'af-memory';
export type MemoryJob =
  | { kind: 'reflect'; runId: string; orgId: string }
  /** Record the run's procedure signature; draft a skill proposal after the 3rd identical one. */
  | { kind: 'skill-learn'; runId: string; orgId: string }
  | { kind: 'consolidate'; orgId?: string; force?: boolean }
  | { kind: 'vault-sync'; orgId?: string };
/** Outbox event (internal) asking the worker for a vault sync. */
export const KNOWLEDGE_SYNC_EVENT = 'knowledge.sync_requested';

// ---------- Routes (spread into ROUTES) ----------
export const KNOWLEDGE_ROUTES = {
  listAgentMemories: { method: 'GET', path: '/api/agents/:id/memories', perm: 'agent:read' },
  getAgentLearning: { method: 'GET', path: '/api/agents/:id/learning', perm: 'agent:read' },
  updateAgentLearning: { method: 'PATCH', path: '/api/agents/:id/learning', perm: 'agent:update' },
  listOrgKnowledge: { method: 'GET', path: '/api/knowledge', perm: 'agent:read' },
  createMemory: { method: 'POST', path: '/api/memories', perm: 'agent:update' },
  updateMemory: { method: 'PATCH', path: '/api/memories/:id', perm: 'agent:update' },
  deleteMemory: { method: 'DELETE', path: '/api/memories/:id', perm: 'agent:update' },
  listMemoryRevisions: { method: 'GET', path: '/api/memories/:id/revisions', perm: 'agent:read' },
  getKnowledgeSettings: { method: 'GET', path: '/api/knowledge/settings', perm: 'agent:read' },
  updateKnowledgeSettings: { method: 'PUT', path: '/api/knowledge/settings', perm: 'agent:update' },
  getVaultStatus: { method: 'GET', path: '/api/knowledge/vault', perm: 'agent:read' },
  syncVault: { method: 'POST', path: '/api/knowledge/vault/sync', perm: 'agent:update' },
  listMasterModels: { method: 'GET', path: '/api/master/models', perm: 'conversation:read' },
  listMasterConversations: { method: 'GET', path: '/api/master/conversations', perm: 'conversation:read' },
  createMasterConversation: {
    method: 'POST',
    path: '/api/master/conversations',
    perm: 'conversation:create',
  },
  updateMasterConversation: {
    method: 'PATCH',
    path: '/api/master/conversations/:id',
    perm: 'conversation:create',
  },
  sendMasterMessage: { method: 'POST', path: '/api/master/conversations/:id/messages', perm: 'agent:run' },
} as const satisfies Record<string, RouteDef>;
